import React from 'react';
import { Speed } from '@mui/icons-material';
import Tooltip from '../utils/Tooltip';
import { useOverlay } from '../../contexts/OverlayContext';
import { STATS } from '../../core/st';
import './BuildScore.css';

const TXT_SCORE = { fr: 'score', en: 'score', de: 'Score', es: 'puntuación', pt: 'pontuação' };
const TXT_INFINITY = String.fromCharCode(0x221E);
const TXT_TOOLTIP_HEADERS = {
    criteria: { fr: 'critère', en: 'criteria', de: 'Kriterium', es: 'criterio', pt: 'critério' },
    value: { fr: 'valeur', en: 'value', de: 'Wert', es: 'valor', pt: 'valor' },
    target: { fr: 'cible', en: 'target', de: 'Ziel', es: 'objetivo', pt: 'alvo' },
    score: { fr: 'score', en: 'score', de: 'Score', es: 'puntuación', pt: 'pontuação' },
};
const statDefsById = new Map(STATS.map((stat) => [Number(stat.id), stat]));

const BUCKET_LABELS = {
    criteria: { fr: 'Critères', en: 'Criteria', de: 'Kriterien', es: 'Criterios', pt: 'Critérios' },
    offensive: { fr: 'Offensif', en: 'Offensive', de: 'Offensiv', es: 'Ofensivo', pt: 'Ofensivo' },
    utility: { fr: 'Utilitaire', en: 'Utility', de: 'Nutzen', es: 'Utilidad', pt: 'Utilidade' },
    stats: { fr: 'Stats', en: 'Stats', de: 'Stats', es: 'Stats', pt: 'Stats' },
    spells: { fr: 'Sorts', en: 'Spells', de: 'Zauber', es: 'Hechizos', pt: 'Feitiços' },
    defense: { fr: 'Défense', en: 'Defense', de: 'Verteidigung', es: 'Defensa', pt: 'Defesa' },
};

const TXT_SCORE_NOTE = {
    fr: 'Le score sert au générateur à évaluer les stuffs vis à vis des critères. Il est affiché à titre indicatif. Il ne représente pas la qualité absolue du stuff.',
    en: 'The score is what the generator uses to rate builds against your criteria. It is shown for guidance only. It does not represent the absolute quality of a build.',
    de: 'Der Score dient dem Generator dazu, Builds anhand deiner Kriterien zu bewerten. Er wird nur zur Orientierung angezeigt. Er stellt nicht die absolute Qualität eines Builds dar.',
    es: 'La puntuación sirve al generador para evaluar las builds según tus criterios. Se muestra solo a título orientativo. No representa la calidad absoluta de una build.',
    pt: 'A pontuação serve ao gerador para avaliar as builds em relação aos teus critérios. É exibida apenas a título indicativo. Não representa a qualidade absoluta de uma build.',
};

const TXT_MOY = { fr: 'moy.', en: 'avg', de: 'Ø', es: 'med.', pt: 'méd.' };
const TXT_SCORE_TOTAL = { fr: 'Score', en: 'Score', de: 'Score', es: 'Puntuación', pt: 'Pontuação' };

const L = (obj, lang) => obj?.[lang] || obj?.en || '';

const formatTermValue = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return n >= 100 ? Math.round(n).toString() : n.toFixed(1).replace(/\.0$/, '');
};

const formatTargetValue = (detail) => {
    if (!detail) return '-';
    if (detail.maximize) return TXT_INFINITY;
    const goal = Number(detail.goal);
    if (!Number.isFinite(goal)) return '-';
    return Math.round(goal).toString();
};

const formatMetricValue = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    return n >= 100 ? Math.round(n).toString() : n.toFixed(1).replace(/\.0$/, '');
};

const formatValueCell = (value) => formatMetricValue(value);

const toStatRow = (detail, idx, lang) => {
    const statDef = statDefsById.get(Number(detail.charId));
    return {
        key: `stat-${detail.charId}-${idx}`,
        label: statDef?.label?.[lang] || statDef?.label?.en || String(detail.charId),
        value: detail.value,
        target: formatTargetValue(detail),
        term: detail.term,
    };
};

const Tag = ({ kind, children }) => (
    <span className={`build-score-tooltip-tag build-score-tooltip-tag--${kind}`}>{children}</span>
);

const renderRows = (rows, weight, nested) => rows.map((row) => (
    <div key={row.key} className="build-score-tooltip-row">
        <span className={`build-score-tooltip-cell build-score-tooltip-cell--criteria${nested ? ' build-score-tooltip-cell--nested' : ''}`}>
            {row.label}
            {row.spellLineIndicator ? (
                <span className="build-score-tooltip-line-indicator">{` ${row.spellLineIndicator}`}</span>
            ) : null}
        </span>
        <span className="build-score-tooltip-cell build-score-tooltip-cell--value">
            {formatValueCell(row.value)}
        </span>
        <span className="build-score-tooltip-cell build-score-tooltip-cell--target">
            {row.target ?? '-'}
        </span>
        <span className="build-score-tooltip-cell build-score-tooltip-cell--score">
            {formatTermValue((Number(row.term) || 0) * weight)}
        </span>
    </div>
));

const bucketTags = (bucket, lang) => {
    const tags = [];
    if (bucket.rows.length > 1) tags.push(<Tag key="moy" kind="moy">{L(TXT_MOY, lang)}</Tag>);
    const mult = Number(bucket.multFactor);
    if (Number.isFinite(mult) && Math.abs(mult - 1) > 0.005) {
        tags.push(<Tag key="mult" kind="mult">{`×${mult.toFixed(1)}`}</Tag>);
    }
    return tags;
};

const groupHeader = (key, label, value, tags, first) => (
    <div
        key={key}
        className={`build-score-tooltip-section-header${first ? ' build-score-tooltip-section-header--first' : ''}`}
    >
        <span className="build-score-tooltip-section-title">
            {label}
            {tags}
        </span>
        <span className="build-score-tooltip-section-value">{formatTermValue(value)}</span>
    </div>
);

const subHeader = (key, label, value, tags) => (
    <div key={key} className="build-score-tooltip-subheader">
        <span className="build-score-tooltip-section-title">
            {label}
            {tags}
        </span>
        <span className="build-score-tooltip-section-value">{formatTermValue(value)}</span>
    </div>
);

/** Shared breakdown panel used by the desktop tooltip and the mobile sheet overlay. */
export const buildScoreDetailsContent = ({
    score = null,
    lang = 'fr',
    breakdown = null,
    spellLineIndicatorMap = null,
}) => {
    const hasScore = score != null && Number.isFinite(score);
    const scoreNum = Number(score);

    const offDetails = breakdown?.offensiveTermDetails;
    const nonOffDetails = breakdown?.nonOffensiveTermDetails;
    const splitProvided = Array.isArray(offDetails) || Array.isArray(nonOffDetails);
    const offensiveRows = splitProvided ? (offDetails || []).map((d, i) => toStatRow(d, i, lang)) : [];
    const utilityRows = splitProvided
        ? (nonOffDetails || []).map((d, i) => toStatRow(d, i, lang))
        : (breakdown?.statTermDetails || []).map((d, i) => toStatRow(d, i, lang));

    const spellRows = (breakdown?.spellTermDetails || [])
        .filter((detail) => !detail?.skipped && Number.isFinite(detail?.term))
        .map((detail, idx) => {
            const lineKeys = Array.from(new Set(
                (detail?.lineKeys || [])
                    .map((x) => String(x || '').trim())
                    .filter(Boolean)
            )).sort();
            const spellSig = Number.isFinite(Number(detail?.spellId)) && lineKeys.length
                ? `${Number(detail.spellId)}|${lineKeys.join('||')}`
                : null;
            return {
                key: `spell-${detail.spellId}-${idx}`,
                label: detail?.name || `Spell ${detail?.spellId ?? idx + 1}`,
                value: detail?.primary,
                target: TXT_INFINITY,
                term: detail.term,
                spellLineIndicator: spellSig ? spellLineIndicatorMap?.get?.(spellSig) : null,
            };
        });

    const defenseRows = (breakdown?.defenseTermDetails || [])
        .filter((detail) => Number.isFinite(detail?.term))
        .map((detail, idx) => ({
            key: `defense-${detail.key || idx}`,
            label: detail?.label?.[lang] || detail?.label?.en || detail?.key || 'Defense',
            value: detail?.value,
            target: '-',
            term: detail.term,
        }));

    const hasOffensive = offensiveRows.length > 0;
    const criteriaBuckets = [];
    if (hasOffensive) {
        criteriaBuckets.push({
            key: 'offensive',
            label: L(BUCKET_LABELS.offensive, lang),
            mean: Number(breakdown?.offensiveMean) || 0,
            rows: offensiveRows,
            multFactor: Number(breakdown?.offensiveMultFactor),
        });
    }
    if (utilityRows.length) {
        criteriaBuckets.push({
            key: 'utility',
            label: L(hasOffensive ? BUCKET_LABELS.utility : BUCKET_LABELS.stats, lang),
            mean: splitProvided ? (Number(breakdown?.nonOffensiveMean) || 0) : (Number(breakdown?.statMean) || 0),
            rows: utilityRows,
        });
    }
    if (spellRows.length) {
        criteriaBuckets.push({
            key: 'spells',
            label: L(BUCKET_LABELS.spells, lang),
            mean: Number(breakdown?.spellMean) || 0,
            rows: spellRows,
        });
    }

    const hasCriteria = criteriaBuckets.length > 0;
    const defenseActive = breakdown?.defenseScore != null && Number.isFinite(Number(breakdown.defenseScore));
    const hasDefense = defenseActive && defenseRows.length > 0;
    const criteriaWeight = Number.isFinite(Number(breakdown?.criteriaWeight)) ? Number(breakdown.criteriaWeight) : 1;
    const baseScore = Number(breakdown?.baseScore);
    const defenseScore = Number(breakdown?.defenseScore);
    const defensePct = hasDefense ? Math.round((Number(breakdown?.defenseShare) || 0) * 100) : null;
    const criteriaPct = hasDefense ? Math.round(criteriaWeight * 100) : null;
    const showHierarchy = hasCriteria || hasDefense;

    if (!showHierarchy) return null;

    const bodyNodes = [];
    if (hasDefense && hasCriteria) {
        const critWeightTag = <Tag key="w" kind="weight">{`${criteriaPct}%`}</Tag>;
        if (criteriaBuckets.length === 1) {
            const b = criteriaBuckets[0];
            bodyNodes.push(groupHeader(
                'crit', L(BUCKET_LABELS.criteria, lang), baseScore,
                [critWeightTag, ...bucketTags(b, lang)], true,
            ));
            bodyNodes.push(...renderRows(b.rows, criteriaWeight, true));
        } else {
            bodyNodes.push(groupHeader('crit', L(BUCKET_LABELS.criteria, lang), baseScore, [critWeightTag], true));
            criteriaBuckets.forEach((b) => {
                bodyNodes.push(subHeader(`sub-${b.key}`, b.label, b.mean * criteriaWeight, bucketTags(b, lang)));
                bodyNodes.push(...renderRows(b.rows, criteriaWeight, true));
            });
        }
        bodyNodes.push(groupHeader(
            'def', L(BUCKET_LABELS.defense, lang), defenseScore,
            [<Tag key="w" kind="weight">{`${defensePct}%`}</Tag>], false,
        ));
        bodyNodes.push(...renderRows(defenseRows, 1, true));
    } else if (hasCriteria) {
        criteriaBuckets.forEach((b, i) => {
            bodyNodes.push(groupHeader(`crit-${b.key}`, b.label, b.mean, bucketTags(b, lang), i === 0));
            bodyNodes.push(...renderRows(b.rows, 1, criteriaBuckets.length > 1));
        });
    } else {
        bodyNodes.push(groupHeader('def', L(BUCKET_LABELS.defense, lang), defenseScore, [], true));
        bodyNodes.push(...renderRows(defenseRows, 1, true));
    }

    const showFooter = hasDefense || criteriaBuckets.length > 1 || (!hasCriteria && hasDefense);
    return (
        <div className="build-score-tooltip column">
            <p className="build-score-tooltip-note">{L(TXT_SCORE_NOTE, lang)}</p>
            <div className="build-score-tooltip-row build-score-tooltip-row--header">
                <span className="build-score-tooltip-cell build-score-tooltip-cell--criteria build-score-tooltip-cell--header">
                    {TXT_TOOLTIP_HEADERS.criteria[lang] || TXT_TOOLTIP_HEADERS.criteria.en}
                </span>
                <span className="build-score-tooltip-cell build-score-tooltip-cell--value build-score-tooltip-cell--header">
                    {TXT_TOOLTIP_HEADERS.value[lang] || TXT_TOOLTIP_HEADERS.value.en}
                </span>
                <span className="build-score-tooltip-cell build-score-tooltip-cell--target build-score-tooltip-cell--header">
                    {TXT_TOOLTIP_HEADERS.target[lang] || TXT_TOOLTIP_HEADERS.target.en}
                </span>
                <span className="build-score-tooltip-cell build-score-tooltip-cell--score build-score-tooltip-cell--header">
                    {TXT_TOOLTIP_HEADERS.score[lang] || TXT_TOOLTIP_HEADERS.score.en}
                </span>
            </div>
            <div className="build-score-tooltip-header-divider" />
            {bodyNodes}
            {showFooter && hasScore ? (
                <div className="build-score-tooltip-footer">
                    <span className="build-score-tooltip-footer-label">{TXT_SCORE_TOTAL[lang] || TXT_SCORE_TOTAL.en}</span>
                    <span className="build-score-tooltip-footer-total">{Math.round(scoreNum)}</span>
                </div>
            ) : null}
        </div>
    );
};

const BuildScore = ({
    score = null,
    lang = 'fr',
    color,
    breakdown = null,
    spellLineIndicatorMap = null,
    compact = false,
    bordered = false,
}) => {
    const hasScore = score != null && Number.isFinite(score);
    const tooltipContent = buildScoreDetailsContent({
        score,
        lang,
        breakdown,
        spellLineIndicatorMap,
    });

    const body = (
        <div
            className={`build-score-badge column${compact ? ' build-score-badge--compact' : ''}${bordered ? ' build-score-badge--bordered' : ''}${tooltipContent ? ' build-score-badge--interactive' : ''}`}
            aria-live="polite"
        >
            <span className="build-score-badge-label">{TXT_SCORE[lang] || TXT_SCORE.en}</span>
            <h3 className="build-score-badge-value" style={color ? { color } : undefined}>
                {hasScore ? Math.round(score) : '-'}
            </h3>
        </div>
    );

    if (!tooltipContent) return body;

    return (
        <Tooltip
            content={tooltipContent}
            className="build-score-tooltip-shell"
            wrapperClassName="build-score-tooltip-wrap"
        >
            {body}
        </Tooltip>
    );
};

/** Mobile gen-sheet row: icon + label left, tappable value opens score details overlay. */
export const BuildScoreSheetRow = ({
    score = null,
    lang = 'fr',
    breakdown = null,
    spellLineIndicatorMap = null,
}) => {
    const { showOverlay } = useOverlay();
    const hasScore = score != null && Number.isFinite(score);
    const label = TXT_SCORE_TOTAL[lang] || TXT_SCORE_TOTAL.en;
    const details = buildScoreDetailsContent({
        score,
        lang,
        breakdown,
        spellLineIndicatorMap,
    });
    const valueText = hasScore ? String(Math.round(score)) : '-';

    const openDetails = () => {
        if (!details) return;
        showOverlay([
            <h6 key="title">{label}</h6>,
            <div key="body" className="build-score-overlay">
                {details}
            </div>,
        ]);
    };

    return (
        <div className="build-score-sheet-row row">
            <span className="mobile-sheet-list__icon-slot">
                <Speed className="build-score-sheet-row__icon" fontSize="small" />
            </span>
            <span className="mobile-sheet-list__label">{label}</span>
            {details ? (
                <button
                    type="button"
                    className="build-score-sheet-row__value"
                    onClick={openDetails}
                    aria-label={label}
                >
                    {valueText}
                </button>
            ) : (
                <span className="build-score-sheet-row__value build-score-sheet-row__value--static">
                    {valueText}
                </span>
            )}
        </div>
    );
};

export default BuildScore;
